import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../DataContext';
import api from '../api';
import Segmented from '../components/Segmented';

const DIRECTUS_URL = 'https://portal.innovabot.com.co';

const statusCfg = {
  Finalizada:    { label: 'Completada',  color: 'var(--success)', bg: 'var(--success-soft)'  },
  Fallida:       { label: 'Fallida',     color: 'var(--danger)', bg: 'var(--danger-soft)' },
  'No contesta': { label: 'No contesta', color: 'var(--warning)', bg: 'rgba(251,146,60,0.1)'  },
  Buzon:         { label: 'Buzón',       color: 'var(--accent)', bg: 'rgba(192,132,252,0.1)' },
  Rechazada:     { label: 'Rechazada',   color: 'var(--danger)', bg: 'rgba(244,63,94,0.1)'   },
};

const sentimentColor = {
  Positivo:'var(--success)', positivo:'var(--success)', positiva:'var(--success)',
  Neutro:'var(--text-2)', neutro:'var(--text-2)', neutral:'var(--text-2)',
  Negativo:'var(--danger)', negativo:'var(--danger)',
};

function getAudioUrl(grabacion) {
  if (!grabacion) return null;
  if (grabacion.startsWith('http')) return grabacion;
  return `${DIRECTUS_URL}/assets/${grabacion}`;
}

function formatFecha(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('es-CO', {
    day:'2-digit', month:'2-digit', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12: true,
  });
}

function formatearTranscripcion(texto) {
  if (!texto) return [];
  return texto.split('\n\n').map(l => l.trim()).filter(Boolean).map(linea => {
    const esAgente  = linea.startsWith('Agente:');
    const esCliente = linea.startsWith('Cliente:');
    let contenido = linea.replace(/^(Agente:|Cliente:)\s*/, '').trim()
      .replace(/^\[['"]?/, '').replace(/['"]?\]$/, '').trim();
    return { rol: esAgente ? 'agente' : esCliente ? 'cliente' : 'otro', texto: contenido };
  }).filter(m => m.texto);
}

// ── Etiqueta HONESTA solo para mostrar (no toca resultado_llamada, status ni cobro) ──
// "Cliente colgó sin resultado" es la etiqueta por defecto que usa el backend cuando
// hubo transcripción de la llamada PERO no se registró un acuerdo/rechazo — aunque el
// deudor nunca haya dicho una palabra (esa etiqueta solo exige que el AGENTE hablara
// 10+ segundos). Aquí, para presentarle al cliente algo fiel a lo que pasó, se revisa
// la transcripción real: si el deudor SÍ habló con contenido (no un monosílabo tipo
// "¿Aló?"), se muestra como "Contestada / Sin acuerdo"; si no dijo nada sustancial,
// se muestra como "Sin respuesta del cliente" — igual que ya se etiquetan los casos
// donde el sistema sabe con certeza que nadie respondió.
function clienteHabloReal(transcripcion) {
  if (!transcripcion) return false;
  const lineasCliente = transcripcion.split('\n\n')
    .map(l => l.trim())
    .filter(l => l.startsWith('Cliente:'))
    .map(l => l.replace(/^Cliente:\s*/, '').trim());
  const texto = lineasCliente.join(' ').trim();
  const soloLetras = texto.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ]/g, '');
  return soloLetras.length >= 6;
}

function etiquetaResultadoVisible(c) {
  const raw = c.resultado_llamada || '';
  if (raw === 'Cliente colgó sin resultado') {
    return clienteHabloReal(c.transcripcion) ? 'Contestada / Sin acuerdo' : 'Sin respuesta del cliente';
  }
  return raw;
}

function exportCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => {
    const val = (r[h]||'').toString().replace(/"/g,"'");
    return `"${val}"`;
  }).join(','));
  const csvContent = [headers.join(','),...rows].join('\n');
  const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], {type:'text/csv;charset=utf-8'});
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = filename; a.click();
}

function AudioPlayer({ url, onDownload }) {
  const [playing, setPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const audioRef = React.useRef(null);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;

  return (
    <div>
      <div style={{ fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12 }}>🎵 Grabación</div>
      <audio ref={audioRef} src={url} key={url}
        onTimeUpdate={e => { setCurrentTime(e.target.currentTime); setProgress(e.target.currentTime/e.target.duration*100||0); }}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => setPlaying(false)}/>
      <div className="ic-card" style={{ background:'var(--bg)', border:'1px solid var(--surface-2)', borderRadius:12, padding:'14px 16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
          <button onClick={togglePlay} style={{
            width:36, height:36, borderRadius:'50%', border:'none', cursor:'pointer',
            background:'linear-gradient(135deg,var(--accent-solid),var(--accent-2))',
            color:'#fff', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center',
            flexShrink:0,
          }}>{playing ? '⏸' : '▶'}</button>
          <div style={{ flex:1 }}>
            <div style={{ position:'relative', height:4, background:'var(--surface-2)', borderRadius:4, cursor:'pointer', marginBottom:6 }}
              onClick={e => {
                if (!audioRef.current) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                audioRef.current.currentTime = pct * duration;
              }}>
              <div style={{ position:'absolute', left:0, top:0, height:'100%', borderRadius:4,
                width:`${progress}%`, background:'linear-gradient(90deg,var(--accent-solid),var(--accent))',
                transition:'width 0.1s',
              }}/>
              <div style={{ position:'absolute', top:'50%', transform:'translateY(-50%)',
                left:`${progress}%`, width:12, height:12, borderRadius:'50%',
                background:'var(--accent)', marginLeft:-6, boxShadow:'0 0 6px rgba(167,139,250,0.6)',
              }}/>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text-3)' }}>
              <span>{fmt(currentTime)}</span>
              <span>{fmt(duration)}</span>
            </div>
          </div>
        </div>
      </div>
      <button onClick={onDownload} style={{
        marginTop:8, padding:'5px 12px', borderRadius:6, cursor:'pointer', fontSize:11,
        border:'1px solid var(--accent-soft-2)', background:'var(--accent-soft)',
        color:'var(--accent)', display:'inline-flex', alignItems:'center', gap:4,
      }}>↓ Descargar</button>
    </div>
  );
}

export default function Calls() {
  const { llamadasFiltradas, campanas, filtroFechaDesde, setFiltroFechaDesde, filtroFechaHasta, setFiltroFechaHasta } = useData();
  const [filtroEstado, setFiltroEstado]     = useState('todos');
  const [filtroBarrido, setFiltroBarrido]   = useState('todos');
  const [filtroCampana, setFiltroCampana]   = useState('todas');
  const [filtroResultado, setFiltroResultado] = useState('todos');
  const [expandido, setExpandido]           = useState(null);
  const [audioKey, setAudioKey]             = useState({});
  const [busqueda, setBusqueda]             = useState('');
  const [seleccionados, setSeleccionados]   = useState(new Set());

  // Opciones únicas para filtros
  const barridosUnicos = useMemo(() => [...new Set(llamadasFiltradas.map(c => c.barrido||1))].sort((a,b)=>a-b), [llamadasFiltradas]);
  const campanasUnicas = useMemo(() => [...new Map(llamadasFiltradas.filter(c=>c.campana_nombre).map(c=>[c.campana_id, c.campana_nombre])).entries()], [llamadasFiltradas]);
  const resultadosUnicos = useMemo(() => [...new Set(llamadasFiltradas.map(c => c.resultado_llamada).filter(Boolean))], [llamadasFiltradas]);

  // Al elegir una campaña, se cargan TODAS sus llamadas del backend en UNA sola consulta
  // paginada (no en cada render), para que la tabla muestre la campaña completa — no solo
  // las 500 recientes del contexto.
  const [llamadasCampana, setLlamadasCampana] = useState(null);
  const [cargandoCampana, setCargandoCampana] = useState(false);
  useEffect(() => {
    let cancel = false;
    if (filtroCampana === 'todas') { setLlamadasCampana(null); return; }
    setCargandoCampana(true);
    api.get(`/calls/export?campana_id=${filtroCampana}`)
      .then(r => { if (!cancel) setLlamadasCampana(r.data?.data || []); })
      .catch(() => { if (!cancel) setLlamadasCampana([]); })
      .finally(() => { if (!cancel) setCargandoCampana(false); });
    return () => { cancel = true; };
  }, [filtroCampana]);

  const baseLlamadas = (filtroCampana !== 'todas' && llamadasCampana) ? llamadasCampana : llamadasFiltradas;

  const llamadas = useMemo(() => baseLlamadas.filter(c => {
    if (filtroEstado !== 'todos' && c.status !== filtroEstado) return false;
    if (filtroBarrido !== 'todos' && String(c.barrido||1) !== String(filtroBarrido)) return false;
    if (filtroCampana !== 'todas' && c.campana_id !== filtroCampana) return false;
    if (filtroResultado !== 'todos' && !(c.resultado_llamada||'').toLowerCase().includes(filtroResultado.toLowerCase())) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      return (
        (c.nombre||'').toLowerCase().includes(q) ||
        (c.telefono||'').toLowerCase().includes(q) ||
        (c.identificacion||'').toLowerCase().includes(q) ||
        (c.resultado_llamada||'').toLowerCase().includes(q) ||
        (c.sentimiento||'').toLowerCase().includes(q) ||
        (c.status||'').toLowerCase().includes(q) ||
        (c.campana_nombre||'').toLowerCase().includes(q) ||
        (c.transcripcion||'').toLowerCase().includes(q)
      );
    }
    return true;
  }), [baseLlamadas, filtroEstado, filtroBarrido, filtroCampana, filtroResultado, busqueda]);

  // Tope de RENDER para no saturar el navegador con campañas grandes: la tabla pinta
  // máximo TABLE_CAP filas; el conteo y el export usan el set completo (llamadas).
  const TABLE_CAP = 500;
  const llamadasVista = useMemo(() => llamadas.slice(0, TABLE_CAP), [llamadas]);

  const toggleRow = (id) => {
    setExpandido(prev => prev === id ? null : id);
    setAudioKey(prev => ({ ...prev, [id]: Date.now() }));
  };

  const toggleSeleccion = (id) => setSeleccionados(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  const toggleTodos = () => setSeleccionados(
    seleccionados.size === llamadas.length ? new Set() : new Set(llamadas.map(c => c.id))
  );

  const [zipEstado, setZipEstado] = useState(null);

  const descargarGrabaciones = async () => {
    const items = seleccionados.size > 0
      ? llamadas.filter(c => seleccionados.has(c.id) && c.grabacion_audio)
      : llamadas.filter(c => c.grabacion_audio);
    if (!items.length) { alert('No hay grabaciones en la selección.'); return; }
    if (items.length > 150) {
      alert(`Seleccionaste ${items.length} grabaciones y el máximo por descarga es 150.\n\n`
          + `Filtra por campaña o selecciona menos para descargarlas por lotes.`);
      return;
    }
    setZipEstado('generando');
    try {
      const { data } = await api.post('/calls/download-zip', { call_ids: items.map(c => c.id) });
      const a = document.createElement('a');
      a.href = data.url; a.download = data.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setZipEstado(null);
      if (data.fallidos > 0) {
        alert(`Descarga lista: ${data.incluidos} grabaciones (${data.tamano_mb} MB).\n`
            + `${data.fallidos} no se pudieron incluir.`);
      }
    } catch (e) {
      setZipEstado(null);
      alert('Error generando la descarga: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const mapExport = (c) => ({
    nombre: c.nombre, identificacion: c.identificacion || '', telefono: c.telefono, duracion: c.duracion_segundos,
    estado: c.status, resultado: c.resultado_llamada, sentimiento: c.sentimiento,
    costo_cop: c.costo_llamada, grabacion_url: getAudioUrl(c.grabacion_audio) || '',
    transcripcion: (c.transcripcion || '').replace(/\n/g, ' | '),
    campana: c.campana_nombre || '', fecha: formatFecha(c.date_updated),
  });

  const exportarSeleccionados = () => {
    // Exporta el set COMPLETO ya cargado (llamadas): si hay campaña seleccionada, son
    // todos sus registros; si no, la vista/lo seleccionado. Sin consulta extra al backend.
    const fuente = seleccionados.size > 0 ? llamadas.filter(c => seleccionados.has(c.id)) : llamadas;
    const nombre = filtroCampana !== 'todas'
      ? `llamadas_${((campanas || []).find(c => c.id === filtroCampana)?.nombre_campana || 'campana').replace(/\s+/g, '_')}.csv`
      : 'llamadas.csv';
    exportCSV(fuente.map(mapExport), nombre);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ fontFamily:'Inter,sans-serif', fontSize:20, fontWeight:700, color:'var(--text)' }}>Historial de llamadas</h1>
          <p style={{ fontSize:12, color:'var(--text-3)', marginTop:2 }}>
            {cargandoCampana ? 'Cargando campaña completa…'
              : filtroCampana !== 'todas'
                ? `${llamadas.length} llamadas${llamadas.length > TABLE_CAP ? ` · mostrando primeras ${TABLE_CAP}, exporta para descargar todas` : ''}`
                : `${llamadas.length} de ${llamadasFiltradas.length} llamadas`}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <input type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)}
            style={{ padding:'6px 10px', background:'var(--surface-3)', border:'1px solid var(--surface)', borderRadius:7, color:'var(--text)', fontSize:11, outline:'none' }}/>
          <span style={{ color:'var(--text-3)', fontSize:11 }}>→</span>
          <input type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)}
            style={{ padding:'6px 10px', background:'var(--surface-3)', border:'1px solid var(--surface)', borderRadius:7, color:'var(--text)', fontSize:11, outline:'none' }}/>
          <button onClick={exportarSeleccionados} disabled={cargandoCampana} style={{
            padding:'6px 14px', borderRadius:7, border:'1px solid var(--surface)',
            background:'var(--surface-3)', color:'var(--text-2)', fontSize:12, cursor: cargandoCampana ? 'wait' : 'pointer',
          }}>↓ {cargandoCampana ? 'Cargando…' : filtroCampana !== 'todas' ? `Exportar campaña (${llamadas.length})` : seleccionados.size > 0 ? `Exportar (${seleccionados.size})` : 'Exportar CSV'}</button>
        <button onClick={descargarGrabaciones} disabled={zipEstado === 'generando'} style={{
          padding:'8px 14px', borderRadius:8, cursor: zipEstado ? 'wait' : 'pointer', fontSize:12,
          border:'1px solid rgba(45,212,191,0.35)', background:'rgba(45,212,191,0.10)',
          color:'#2dd4bf', marginLeft:8, opacity: zipEstado ? 0.6 : 1,
        }}>
          {zipEstado === 'generando' ? 'Preparando archivo...' : '↓ Descargar grabaciones'}
        </button>
        </div>
      </div>

      {/* Filtros estado */}
      <div style={{ marginBottom:8 }}>
        <Segmented size="sm"
          options={[['todos','Todas'],['Finalizada','Completadas'],['Fallida','Fallidas'],['No contesta','No contesta'],['Buzon','Buzón'],['Rechazada','Rechazadas']].map(([value,label])=>({value,label}))}
          value={filtroEstado} onChange={setFiltroEstado} />
      </div>
      {/* Filtros adicionales */}
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
        <select value={filtroBarrido} onChange={e => setFiltroBarrido(e.target.value)} style={{
          padding:'5px 10px', borderRadius:8, border:'1px solid var(--surface-2)', background:'var(--surface-3)',
          color:'var(--text-2)', fontSize:12, cursor:'pointer', outline:'none'
        }}>
          <option value="todos">Todos los barridos</option>
          {barridosUnicos.map(b => <option key={b} value={b}>Barrido #{b}</option>)}
        </select>
        <select value={filtroCampana} onChange={e => setFiltroCampana(e.target.value)} style={{
          padding:'5px 10px', borderRadius:8, border:'1px solid var(--surface-2)', background:'var(--surface-3)',
          color:'var(--text-2)', fontSize:12, cursor:'pointer', outline:'none'
        }}>
          <option value="todas">Todas las campañas</option>
          {(campanas && campanas.length
            ? campanas.map(c => [c.id, c.nombre_campana || c.nombre || c.id])
            : campanasUnicas).map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
        </select>
        <select value={filtroResultado} onChange={e => setFiltroResultado(e.target.value)} style={{
          padding:'5px 10px', borderRadius:8, border:'1px solid var(--surface-2)', background:'var(--surface-3)',
          color:'var(--text-2)', fontSize:12, cursor:'pointer', outline:'none'
        }}>
          <option value="todos">Todos los resultados</option>
          {resultadosUnicos.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        {(filtroBarrido !== 'todos' || filtroCampana !== 'todas' || filtroResultado !== 'todos') && (
          <button onClick={() => { setFiltroBarrido('todos'); setFiltroCampana('todas'); setFiltroResultado('todos'); }} style={{
            padding:'5px 10px', borderRadius:8, border:'1px solid rgba(248,113,113,0.3)',
            background:'var(--danger-soft)', color:'var(--danger)', fontSize:12, cursor:'pointer'
          }}>Limpiar filtros</button>
        )}
      </div>

      {/* Buscador */}
      <div style={{ marginBottom:16, position:'relative' }}>
        <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, teléfono, resultado, transcripción..."
          style={{ width:'100%', padding:'9px 36px', background:'var(--bg)', border:'1px solid var(--surface)',
            borderRadius:9, color:'var(--text)', fontSize:13, outline:'none', fontFamily:'Inter,sans-serif' }}/>
        <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)', fontSize:14 }}>⌕</span>
        {busqueda && <span onClick={() => setBusqueda('')} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)', cursor:'pointer', fontSize:16 }}>×</span>}
      </div>

      {/* Tabla */}
      <div className="ic-card" style={{ background:'var(--bg)', border:'1px solid var(--surface)', borderRadius:12, overflowX:'auto' }}>

        {/* Cabecera */}
        <div style={{ display:'grid', gridTemplateColumns:'36px 1fr 90px 70px 80px 90px 120px 60px 100px 140px', minWidth:980, padding:'10px 16px', background:'var(--surface-3)', borderBottom:'1px solid var(--surface)', alignItems:'center' }}>
          <input type="checkbox" checked={seleccionados.size === llamadas.length && llamadas.length > 0}
            onChange={toggleTodos} style={{ cursor:'pointer', accentColor:'var(--accent-solid)', width:15, height:15 }}/>
          {['Contacto','Teléfono','Dur.','Estado','Ánimo','Resultado','Barrido','Campaña','Fecha'].map(h => (
            <span key={h} style={{ fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.08em' }}>{h}</span>
          ))}
        </div>

        {llamadas.length === 0 && (
          <div style={{ padding:32, textAlign:'center', color:'var(--text-3)', fontSize:13 }}>No hay llamadas</div>
        )}

        {llamadasVista.map(c => {
          const cfg = statusCfg[c.status] || { label:c.status, color:'var(--text-2)', bg:'rgba(148,163,184,0.1)' };
          const abierto   = expandido === c.id;
          const audioUrl  = getAudioUrl(c.grabacion_audio);
          const mensajes  = formatearTranscripcion(c.transcripcion);
          const seleccionado = seleccionados.has(c.id);

          return (
            <div key={c.id} style={{ borderBottom:'1px solid var(--surface-3)' }}>
              {/* Fila */}
              <div style={{ display:'grid', gridTemplateColumns:'36px 1fr 90px 70px 80px 90px 120px 60px 100px 140px', minWidth:980, padding:'12px 16px', alignItems:'center', cursor:'pointer', background: abierto ? 'rgba(124,58,237,0.05)' : seleccionado ? 'rgba(124,58,237,0.03)' : 'transparent', transition:'background 0.15s' }}>
                <input type="checkbox" checked={seleccionado} onChange={() => toggleSeleccion(c.id)}
                  onClick={e => e.stopPropagation()}
                  style={{ cursor:'pointer', accentColor:'var(--accent-solid)', width:15, height:15 }}/>
                <div onClick={() => toggleRow(c.id)} style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background: abierto ? 'var(--accent-solid)' : 'var(--surface-2)', transition:'background 0.2s', flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:'var(--text)' }}>{c.nombre}</div>
                    {c.costo_llamada && <div style={{ fontSize:10, color:'var(--warning)', marginTop:1 }}>${parseFloat(c.costo_llamada).toLocaleString('es-CO')} COP</div>}
                  </div>
                </div>
                <span onClick={() => toggleRow(c.id)} style={{ fontSize:12, color:'var(--text-3)' }}>{c.telefono}</span>
                <span onClick={() => toggleRow(c.id)} style={{ fontSize:12, color:'var(--text-2)' }}>{c.duracion_segundos || '-'}</span>
                <span onClick={() => toggleRow(c.id)} style={{ padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:500, color:cfg.color, background:cfg.bg, width:'fit-content' }}>{cfg.label}</span>
                <span onClick={() => toggleRow(c.id)} style={{ fontSize:12, color: sentimentColor[c.sentimiento] || 'var(--text-3)' }}>{c.sentimiento || '-'}</span>
                <span onClick={() => toggleRow(c.id)} style={{ fontSize:11, color:'var(--text-2)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={etiquetaResultadoVisible(c)||'-'}>{c.resultado_llamada ? etiquetaResultadoVisible(c).replace('Acuerdo: ','').replace('Rechazo: ','') : '-'}</span>
                <span onClick={() => toggleRow(c.id)} style={{ fontSize:12, color:'var(--accent-solid)', fontWeight:600, textAlign:'center' }}>#{c.barrido || 1}</span>
                <span onClick={() => toggleRow(c.id)} style={{ fontSize:11, color:'var(--text-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={c.campana_nombre||'-'}>{c.campana_nombre ? c.campana_nombre.substring(0,12)+(c.campana_nombre.length>12?'...':'') : '-'}</span>
                <div onClick={() => toggleRow(c.id)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'var(--text-3)' }}>{formatFecha(c.date_updated)}</span>
                  <span style={{ fontSize:13, color:'var(--text-3)', transition:'transform 0.2s', transform: abierto ? 'rotate(180deg)' : 'none' }}>⌄</span>
                </div>
              </div>

              {/* Acordeón */}
              {abierto && (
                <div style={{ borderTop:'1px solid var(--accent-soft)', background:'var(--bg)', padding:'20px 24px' }}>
                  <div className="r-apila" style={{ display:'grid', gridTemplateColumns: audioUrl ? '1fr 1fr' : '1fr', gap:20, marginBottom: mensajes.length ? 20 : 0 }}>
                    {/* Detalles */}
                    <div>
                      <div style={{ fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12 }}>Detalles</div>
                      <div className="r-apila" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                        {[
                          { label:'Duración',    value: c.duracion_segundos || '-' },
                          { label:'Costo',       value: c.costo_llamada ? `$${parseFloat(c.costo_llamada).toLocaleString('es-CO')} COP` : '-' },
                          { label:'Sentimiento', value: c.sentimiento || '-' },
                          { label:'Estado',      value: cfg.label },
                        ].map((m,i) => (
                          <div key={i} style={{ background:'var(--bg)', borderRadius:8, padding:'10px 12px' }}>
                            <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:3 }}>{m.label}</div>
                            <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{m.value}</div>
                          </div>
                        ))}
                      </div>
                      {c.resultado_llamada && (
                        <div style={{ background:'var(--bg)', borderRadius:8, padding:'10px 12px' }}>
                          <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:3 }}>Resultado</div>
                          <div style={{ fontSize:12, color:'var(--text-2)' }}>{etiquetaResultadoVisible(c)}</div>
                        </div>
                      )}
                    </div>

                    {/* Audio personalizado */}
                    {audioUrl && <AudioPlayer url={`${audioUrl}?t=${audioKey[c.id]||''}`} onDownload={() => {
                      const a = document.createElement('a'); a.href=`${audioUrl}?t=${Date.now()}`; a.download='grabacion.wav'; a.click();
                    }}/>}
                  </div>

                  {/* Transcripción */}
                  {mensajes.length > 0 && (
                    <div>
                      <div style={{ fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12 }}>💬 Transcripción</div>
                      <div style={{ maxHeight:260, overflowY:'auto', display:'flex', flexDirection:'column', gap:8 }}>
                        {mensajes.map((m, i) => (
                          <div key={i} style={{ display:'flex', justifyContent: m.rol==='agente' ? 'flex-start' : 'flex-end' }}>
                            <div style={{
                              maxWidth:'70%', padding:'8px 14px', borderRadius:12, fontSize:12, lineHeight:1.5,
                              background: m.rol==='agente' ? 'var(--accent-soft)' : 'var(--success-soft)',
                              color: m.rol==='agente' ? 'var(--accent)' : 'var(--success)',
                              borderBottomLeftRadius: m.rol==='agente' ? 4 : 12,
                              borderBottomRightRadius: m.rol==='cliente' ? 4 : 12,
                            }}>
                              <div style={{ fontSize:9, opacity:0.6, marginBottom:3, textTransform:'uppercase' }}>
                                {m.rol==='agente' ? '🤖 Agente IA' : ' Cliente'}
                              </div>
                              {m.texto}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
